import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const shapes = [
    { emoji: "🔵", name: "Blue Circle" },
    { emoji: "🟥", name: "Red Square" },
    { emoji: "🟩", name: "Green Square" },
    { emoji: "🟠", name: "Orange Circle" },
    { emoji: "⭐", name: "Star" },
    { emoji: "⬜", name: "White Square" }
];

const CountingShapesGame: React.FC = () => {
    const [question, setQuestion] = useState("Count the shapes:");
    const [shapeList, setShapeList] = useState<string[]>([]);
    const [targetShape, setTargetShape] = useState<{ emoji: string; name: string } | null>(null);
    const [correctCount, setCorrectCount] = useState<number>(0);
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Counting Shapes Worksheet for LKG | Learn Numbers & Geometry | Mama’s Guidance";
    }, []);

    const generateQuestion = () => {
        const mixedShapes = Array.from({ length: 20 }, () => shapes[Math.floor(Math.random() * shapes.length)]);
        const selectedShape = mixedShapes[Math.floor(Math.random() * mixedShapes.length)];
        const count = mixedShapes.filter(shape => shape.emoji === selectedShape.emoji).length;

        setShapeList(mixedShapes.map(shape => shape.emoji));
        setTargetShape(selectedShape);
        setCorrectCount(count);

        // Generate unique incorrect options
        const incorrectOptions = new Set<number>();
        while (incorrectOptions.size < 2) {
            const randomCount = Math.floor(Math.random() * 10) + 1;
            if (randomCount !== count) {
                incorrectOptions.add(randomCount);
            }
        }

        const optionsArray = shuffleArray([count, ...Array.from(incorrectOptions)]);
        setOptions(optionsArray);
        setMessage("");
    };

    const shuffleArray = (array: number[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const checkAnswer = (selected: number) => {
        if (selected === correctCount) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
            }, 5000)
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Develop counting and shape recognition skills in LKG kids with engaging activities on Mama’s Guidance—featuring fun exercises that combine basic geometry and numeracy through interactive shape-counting tasks.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Counting Shapes</h1>

                                    <p>
                                        Counting shapes is a great way to help preschoolers connect two early math concepts—<strong>numbers</strong> and <strong>shapes</strong>. This LKG worksheet lets your child practice counting different shapes <strong>like circles, squares, triangles, and rectangles</strong> while improving both their number sense and shape recognition skills.
                                    </p>

                                    <h2>🔵 Why Counting Shapes Matters</h2>
                                    <p>
                                        When children count how many times a shape appears, they improve:
                                    </p>
                                    <ul>
                                        <li>Shape identification (<strong>e.g., spotting circles and squares</strong>)</li>
                                        <li>Counting from 1 to 10 (and beyond)</li>
                                        <li>Matching quantities to written numbers</li>
                                        <li>Visual and spatial awareness</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify basic <strong>2D shapes – circle, square, triangle, rectangle, oval</strong></li>
                                        <li>Count the number of each shape shown</li>
                                        <li>Write the correct number or match with number labels</li>
                                        <li>Understand the relationship between shape and quantity</li>
                                    </ul>

                                    <h2>📋 What’s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Count and write how many of each shape appear in a group</li>
                                        <li>Match shapes to the correct number</li>
                                        <li>Color-coded visual guidance to avoid confusion</li>
                                        <li>Use of everyday objects <strong>like balls, boxes, and signs for real-world shape learning</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                    <ul>
                                        <li>Use building blocks or shape toys and ask your child to count them</li>
                                        <li>Make shape charts at home with shape cutouts and numbers</li>
                                        <li>Encourage your child to say the shape name out loud as they count</li>
                                        <li>Play <strong>“Shape Hunt”</strong> games – <strong>“Can you find 3 rectangles in this room?”</strong></li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Turn counting shapes into a storytelling activity—<strong>“Five triangles are dancing at the party, and two circles just arrived!”</strong> Let their imagination learn along with numbers.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Counting Shapes</h1>

                                <p>
                                    Counting shapes is a great way to help preschoolers connect two early math concepts—<strong>numbers</strong> and <strong>shapes</strong>. This LKG worksheet lets your child practice counting different shapes <strong>like circles, squares, triangles, and rectangles</strong> while improving both their number sense and shape recognition skills.
                                </p>

                                <h2>🔵 Why Counting Shapes Matters</h2>
                                <p>
                                    When children count how many times a shape appears, they improve:
                                </p>
                                <ul>
                                    <li>Shape identification (<strong>e.g., spotting circles and squares</strong>)</li>
                                    <li>Counting from 1 to 10 (and beyond)</li>
                                    <li>Matching quantities to written numbers</li>
                                    <li>Visual and spatial awareness</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify basic <strong>2D shapes – circle, square, triangle, rectangle, oval</strong></li>
                                    <li>Count the number of each shape shown</li>
                                    <li>Write the correct number or match with number labels</li>
                                    <li>Understand the relationship between shape and quantity</li>
                                </ul>

                                <h2>📋 What’s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Count and write how many of each shape appear in a group</li>
                                    <li>Match shapes to the correct number</li>
                                    <li>Color-coded visual guidance to avoid confusion</li>
                                    <li>Use of everyday objects <strong>like balls, boxes, and signs for real-world shape learning</strong></li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use building blocks or shape toys and ask your child to count them</li>
                                    <li>Make shape charts at home with shape cutouts and numbers</li>
                                    <li>Encourage your child to say the shape name out loud as they count</li>
                                    <li>Play <strong>“Shape Hunt”</strong> games – <strong>“Can you find 3 rectangles in this room?”</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Turn counting shapes into a storytelling activity—<strong>“Five triangles are dancing at the party, and two circles just arrived!”</strong> Let their imagination learn along with numbers.
                                </p>
                            </article>
                            <h2>How many {targetShape?.name}?</h2>
                            <div>
                                <h3>{shapeList.join(" ")}</h3>
                            </div>
                            <div className="mt-4 mb-5" style={{ display: 'flex', gap: '30px' }}>
                                {options.map((option, index) => (
                                    <div className="countingObjectHeading background-blue" style={{ width: 'auto', padding: '15px' }} key={index} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default CountingShapesGame;
